import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Box, Flex, Group, Tabs } from '@mantine/core';

import { useGetPatientQuery } from '@/store/slices/api/patientsApiSlice';
import { useDeviceAndLeadsRecallFeatureFlag } from '@/hooks/useDeviceAndLeadsRecallFeatureFlag';
import AccessRestricted from '@/components/AccessRestricted';

import { showsInsuranceTab } from '@/types/insurance';

import ClinicalNotes from './ClinicalNotes';
import Devices from './Devices';
import Documents from './Documents';
import EnrollmentGapIndicator, { EnrollmentStatusProvider } from './EnrollmentGapIndicator';
import Insurance from './Insurance';
import PatientActivity from './PatientActivity';
import PatientDetails from './PatientDetails';
import styles from './PatientOverviewPage.module.scss';
import PatientReportsList from './PatientsReportList';
import Recalls from './Recalls';
import ScheduleList from './ScheduleList';

enum PatientTabs {
  Devices = 'devices',
  Recalls = 'recalls',
  Reports = 'reports',
  Activity = 'activity',
  Notes = 'notes',
  Documents = 'documents',
  Insurance = 'insurance'
}

interface TabConfig {
  id: PatientTabs;
  label: string;
  gapSection?: 'device' | 'insurance';
}

const PatientOverviewPage = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const [searchParams] = useSearchParams();
  const { data: patient, error } = useGetPatientQuery(patientId, { skip: !patientId });
  const { isRecallListPageEnabled } = useDeviceAndLeadsRecallFeatureFlag();

  const [activeTab, setActiveTab] = useState<PatientTabs>(PatientTabs.Reports);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'devices') setActiveTab(PatientTabs.Devices);
    if (tab === 'reports') setActiveTab(PatientTabs.Reports);
    if (tab === 'insurance') setActiveTab(PatientTabs.Insurance);
  }, [searchParams]);

  const isAccessDenied =
    error && 'status' in error && (error.status === 401 || error.status === 403);

  const tabs: TabConfig[] = [
    { id: PatientTabs.Devices, label: 'Devices', gapSection: 'device' },
    ...(isRecallListPageEnabled ? [{ id: PatientTabs.Recalls, label: 'Recalls' }] : []),
    { id: PatientTabs.Reports, label: 'Reports' },
    { id: PatientTabs.Activity, label: 'Activity' },
    { id: PatientTabs.Notes, label: 'Notes' },
    { id: PatientTabs.Documents, label: 'Documents' },
    ...(showsInsuranceTab(patient?.clinic?.billingType)
      ? [{ id: PatientTabs.Insurance, label: 'Insurance', gapSection: 'insurance' as const }]
      : [])
  ];

  const renderContent = () => {
    switch (activeTab) {
      case PatientTabs.Devices:
        return (
          <Devices
            isDeviceModalOpen={isDeviceModalOpen}
            setIsDeviceModalOpen={setIsDeviceModalOpen}
          />
        );
      case PatientTabs.Recalls:
        return <Recalls patientId={patientId} />;
      case PatientTabs.Reports:
        return <PatientReportsList />;
      case PatientTabs.Activity:
        return <PatientActivity patientId={patientId} />;
      case PatientTabs.Notes:
        return <ClinicalNotes />;
      case PatientTabs.Documents:
        return <Documents />;
      case PatientTabs.Insurance:
        return <Insurance patientId={patientId} />;
    }
  };

  if (isAccessDenied) {
    return <AccessRestricted />;
  }

  return (
    <EnrollmentStatusProvider patientId={patientId}>
      <Flex direction="column" className={styles.container}>
        <PatientDetails patientId={patientId} />

        <Flex bg="white" className={styles.body}>
          <Flex flex={2} direction="column" className={styles.leftSection}>
            <Box px={24} className={styles.headerSectionContent}>
              <Flex justify="space-between" align="center">
                <Tabs
                  variant="none"
                  value={activeTab}
                  onChange={(value) => value && setActiveTab(value as PatientTabs)}
                  className={styles.tabs}>
                  <Tabs.List className={styles.tabsList}>
                    {tabs.map(({ id, label, gapSection }) => (
                      <Tabs.Tab key={id} value={id} className={styles.tab}>
                        <Group gap={4} wrap="nowrap">
                          {label}
                          {gapSection && <EnrollmentGapIndicator section={gapSection} />}
                        </Group>
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                </Tabs>
              </Flex>
            </Box>
            <Box
              px={24}
              py={16}
              className={`${styles.panel} ${activeTab === PatientTabs.Activity ? styles.panelPinned : ''}`}>
              {renderContent()}
            </Box>
          </Flex>

          <Flex flex={1} direction="column" className={styles.rightSection} mih={0}>
            <ScheduleList />
          </Flex>
        </Flex>
      </Flex>
    </EnrollmentStatusProvider>
  );
};

export default PatientOverviewPage;
